import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { getCourtsFreeFor } from './buildAvailability';
import { BookingSlot } from './types';
import { useVenueAvailability } from './useVenueAvailability';
import {
  getSelectedMinutes,
  getSelectedSlots,
  getSelectionTimeLabel,
  toggleSlotSelection,
} from './utils';

interface Params {
  venueId: string;
  venueName: string;
  timezone?: string | null;
  /** Where a feed link pointed, e.g. `2026-10-11` and a slot id from that day. */
  initialDate?: string;
  initialSlotId?: string;
}

/**
 * Everything the court-booking surfaces share: which day is open, which slots
 * are picked, which court, and the derived labels. Totals come from the quote
 * endpoint, since only the server prices.
 */
export const useCourtBooking = ({
  venueId,
  venueName,
  timezone,
  initialDate,
  initialSlotId,
}: Params) => {
  const source = useVenueAvailability({ venueId, venueName, timezone });
  const { availability, days, loadDate } = source;

  const [selectedDate, setSelectedDate] = useState(initialDate ?? '');
  const [selectedSlotIds, setSelectedSlotIds] = useState<string[]>(
    initialSlotId ? [initialSlotId] : [],
  );
  const [selectedCourtId, setSelectedCourtId] = useState<string | null>(null);

  const isOffered = availability.dates.some((date) => date.localDate === selectedDate);
  const activeDate = (isOffered && selectedDate) || availability.dates[0]?.localDate || '';
  useEffect(() => {
    loadDate(activeDate);
  }, [activeDate, loadDate]);

  const slots: BookingSlot[] = availability.slotsByDate[activeDate] ?? [];
  const selectedSlots = getSelectedSlots(slots, selectedSlotIds);
  const selectedMinutes = getSelectedMinutes(slots, selectedSlotIds);

  const changeDate = (localDate: string) => {
    setSelectedDate(localDate);
    // A slot id is scoped to its day, so carrying a selection across would point
    // at times the player never saw.
    setSelectedSlotIds([]);
    setSelectedCourtId(null);
  };

  const toggleSlot = (slotId: string) => {
    setSelectedSlotIds((previous) => toggleSlotSelection(slots, previous, slotId));
    setSelectedCourtId(null);
  };

  const reset = () => {
    setSelectedSlotIds([]);
    setSelectedCourtId(null);
  };

  const first = selectedSlots[0];
  const last = selectedSlots[selectedSlots.length - 1];
  const startLocal = first ? formatInTimeZone(first.startAt, availability.timezone, 'HH:mm') : '';
  const endLocal = last ? formatInTimeZone(last.endAt, availability.timezone, 'HH:mm') : '';

  // Only courts free for the whole stretch are offered once a stretch is picked.
  const courts = useMemo(
    () =>
      first && last
        ? getCourtsFreeFor(days[activeDate], startLocal, endLocal)
        : availability.courts,
    [first, last, days, activeDate, startLocal, endLocal, availability.courts],
  );
  const selectedCourt = courts.find((court) => court.id === selectedCourtId) ?? null;

  return {
    availability: { ...availability, courts },
    slots,
    selectedDate: activeDate,
    selectedSlotIds,
    selectedCourtId,
    selectedCourt,
    selectedSlots,
    selectedMinutes,
    /** Venue-local HH:mm of the picked stretch, what checkout and the quote take. */
    startLocal,
    /** The club's minimum is met, so the selection can go to court choice. */
    isSelectionValid: selectedMinutes >= availability.minimumMinutes,
    /** `Sunday, September 19` */
    dateLabel: activeDate ? format(parseISO(activeDate), 'EEEE, MMMM d') : '',
    /** `8 – 9 PM` */
    timeLabel: getSelectionTimeLabel(slots, selectedSlotIds),
    isLoading: source.isLoading,
    isBookable: source.isBookable,
    notBookableReason: source.notBookableReason,
    hasFailed: source.hasFailed,
    changeDate,
    toggleSlot,
    selectCourt: setSelectedCourtId,
    reset,
  };
};

export type CourtBooking = ReturnType<typeof useCourtBooking>;
